import {
  ApolloClient,
  ApolloLink,
  type DocumentNode,
  type ErrorPolicy,
  type FetchPolicy,
  type FetchResult,
  InMemoryCache,
  type MutationOptions,
  type NextLink,
  type Observable,
  type Operation,
  type QueryOptions,
  type ServerError,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename';
import { RetryLink } from '@apollo/client/link/retry';
import { isQueryOperation } from '@apollo/client/utilities';
// @ts-expect-error - apollo-upload-client doesn't have proper TypeScript definitions
import createUploadLink from 'apollo-upload-client/public/createUploadLink';
import { type FieldNode, visit } from 'graphql';
import { pick } from 'lodash';

import { isApplicationModeInfinity } from 'src/data/system';
import { getApplicationSessionId } from 'src/lib/app';
import { Authentication } from 'src/lib/Authentication';
import * as env from 'src/lib/env';
import { isAbortError } from 'src/lib/errors';
import { type ACDebugContext, getGqlOperationName, prepareACDebugContext } from 'src/lib/graphql';
import * as gtm from 'src/lib/gtm';
import { type GtmEventName } from 'src/lib/gtm';
import pubsub, { PUBSUB } from 'src/lib/pubsub';
import { captureMessage } from 'src/lib/sentry';
import { saveSignInReturnPath } from 'src/lib/signInReturnPath';
import { ROUTES } from 'src/routes';

// This variable will be used to pass customer ID to backend
let currentCustomerId: string | undefined;
pubsub.subscribe(PUBSUB.CUSTOMER_ID_CHANGED, (_, { customerId }) => {
  currentCustomerId = customerId;
});

// Should contain same values as backend/app/controllers/generic_graphql_controller.rb
const QUERIES_WITHOUT_AUTHENTICATION: readonly string[] = [
  'Authenticate',
  'SignUp',
  'ConfirmRegistration',
  'ConfirmTokenLogin',
  'SendResetPasswordRequest',
  'ResetPassword',
  'AcceptUserInvitation',
  'InviteDetails',
  'ViewSharedDocument',
] as const;

const queryRequiresAuth = (operationName?: string): boolean =>
  operationName !== undefined && !QUERIES_WITHOUT_AUTHENTICATION.includes(operationName);

const requestLink = new ApolloLink(
  (operation: Operation, forward: NextLink): Observable<FetchResult> => {
    // "auth" token can be passed in context, or we should use the global one
    const { authToken }: { authToken?: string } = operation.getContext();
    const token = Authentication.getToken();

    operation.setContext(({ headers = {} }: { headers?: Record<string, string> }) => ({
      headers: {
        ...headers,
        // It is useful to have FE version in BE logs
        'X-Frontend-Version': env.getRepositoryTag() || '',
        // Authentication token
        'X-Access-Token': authToken || token || '',
        // Current customer ID
        'X-Anyword-Customer-Id': currentCustomerId || '',
        // Application type, we are using this to tell requests from platform, extension and "embedded" platform apart
        'X-Application-Type': isApplicationModeInfinity() ? 'extension-platform' : 'platform',
        // Session ID, to be able to track same sessions in logs
        'X-Session-Id': getApplicationSessionId(),
      },
    }));

    return forward(operation);
  }
);

const MAX_QUERY_RETRIES = 5;
const QUERIES_WITHOUT_RETRY: readonly string[] = [
  'IntegrationAssetsResults',
  'IntegrationPerformanceData',
  'UsageReportDashboard',
  'UsageReportWeeklyTrends',
] as const;

/**
 * Retry link, which will retry failed "queries" (not "mutations") up to 5 times.
 * Only Network errors will be retried.
 */
const retryLink = new RetryLink({
  attempts: (count: number, operation: Operation, error?: Error): boolean => {
    // Don't retry if we don't have error
    if (!error) {
      return false;
    }

    // We care only about actual network errors (when we didn't get any response from BE)
    if (error.name === 'ServerError') {
      return false;
    }

    // We should retry only queries, not mutations
    if (!isQueryOperation(operation.query)) {
      return false;
    }

    // Some queries are heavy on BE resources and should not be retried
    if (QUERIES_WITHOUT_RETRY.includes(operation.operationName)) {
      return false;
    }

    // Try only 5 times
    return count < MAX_QUERY_RETRIES;
  },
});

/**
 * This link will remove "__typename" from all variables in GQL operations
 */
const removeTypenameLink = removeTypenameFromVariables();

/**
 * Middleware to trace AC calls params, which will be piped to browser console
 */
const traceAuthorCompleteLink = new ApolloLink(
  (operation: Operation, forward: NextLink): Observable<FetchResult> => {
    const acDebugContext = operation.getContext() as ACDebugContext;
    if (!acDebugContext.traceAuthorComplete) {
      return forward(operation);
    }

    operation.setContext(({ headers = {} }: { headers?: Record<string, string> }) => ({
      headers: {
        ...headers,
        'author-complete-tracing-enabled': true,
      },
    }));

    return forward(operation).map((response: FetchResult): FetchResult => {
      const context = operation.getContext() as { response?: { headers?: Headers } };
      const { response: { headers } = {} } = context;

      if (headers) {
        const traceId = headers.get('author-complete-trace-id');
        if (traceId) {
          pubsub.publish(PUBSUB.TRACE_AC_CALL, {
            traceId,
            title: acDebugContext.traceAuthorCompleteTitle || '',
          });
        }
      }

      return response;
    });
  }
);

const httpLink = createUploadLink({
  uri: `${env.getBackendUrl()}/graphql`,
  credentials: 'same-origin',
});

const errorsInclude = (errors: readonly Error[], text: string): boolean =>
  errors.some(({ message }) => message.includes(text));

const sendMessageToSentry = (
  operation: Operation,
  error: Error,
  message: string,
  extra: Record<string, unknown> = {}
): void => {
  captureMessage(message, {
    level: 'info',
    extra: {
      error: error.toString(),
      source: 'ApolloClient.sendMessageToSentry',
      graphql: pick(operation, ['operationName', 'variables']),
      ...extra,
    },
  });
};

const sendEventToGTM = (event: string, operation: Operation): void => {
  gtm.pushDataLayerEvent(event as GtmEventName, {
    graphql: pick(operation, ['operationName', 'variables']),
  });
};

const rewriteErrorMessage = (error: Error, message: string): void => {
  try {
    error.message = message;
  } catch {
    // do nothing
  }
};

/**
 * Configure an "error link" to handle various error cases.
 * Note `console.warn` calls - Sentry provides a log of console messages with each error, so these might be useful.
 */
const errorHandlerLink = onError(({ operation, response, networkError, graphQLErrors }) => {
  if (networkError) {
    const errorStatusCode = (networkError as ServerError).statusCode;

    // In case of 401 error, logout and navigate to sign-in page
    if (errorStatusCode === 401) {
      // Post a warning to browser console
      // eslint-disable-next-line no-console
      console.warn('Anyword: network request failed with status 401', { operation, networkError });
      // Post event to GTM
      sendEventToGTM(gtm.EVENTS.systemError401, operation);

      // We don't want any errors to propagate to UI from here
      if (response) {
        (response as any).errors = null;
      }

      Authentication.signOut();
      if (!isApplicationModeInfinity()) {
        saveSignInReturnPath(window.location.pathname, window.location.search);
        window.location.replace(ROUTES.SIGN_IN);
      }
      return;
    }

    // In case of 403 error, logout and navigate to verification page
    if (errorStatusCode === 403 && Authentication.isAuthenticated()) {
      // Post a warning to browser console
      // eslint-disable-next-line no-console
      console.warn('Anyword: network request failed with status 403', { operation, networkError });

      if (window.location.pathname !== ROUTES.NOT_VERIFIED && !isApplicationModeInfinity()) {
        window.location.replace(ROUTES.NOT_VERIFIED);
        return;
      }

      return;
    }

    // If we have any 'statusCode' then BE was reached and we've got some response
    if (errorStatusCode > 0) {
      // If failed query requires signed-in state, but we are signed-out, it's probably after-logout draining query.
      //   We will be ignoring these in UI - just adding informational messages to Sentry.
      if (queryRequiresAuth(operation.operationName) && !Authentication.isAuthenticated()) {
        sendMessageToSentry(
          operation,
          networkError,
          'Error in auth-required GraphQL call, which was made in signed-out state'
        );

        // We don't want any errors to propagate to UI from here
        if (response) {
          (response as any).errors = null;
        }
        return;
      }

      // Post a warning to browser console
      // eslint-disable-next-line no-console
      console.warn(`Anyword: network request failed with status ${errorStatusCode}`, {
        operation,
        networkError,
      });
      // Post event to GTM
      sendEventToGTM(gtm.EVENTS.systemError500, operation);

      return;
    }

    // ApolloClient v3 started pushing "abort errors" through error link as well - do not report them
    if (isAbortError(networkError)) {
      return;
    }

    // eslint-disable-next-line no-console
    console.warn('Anyword: possible connection issue', {
      online: window.navigator.onLine,
    });

    // Try rewriting error message to our generic one
    rewriteErrorMessage(networkError, 'Please check your connection and try again.');
  }

  if (graphQLErrors && !isApplicationModeInfinity()) {
    // If we've got "Your trial has expired" error - navigate to "trial ended" page
    if (errorsInclude(graphQLErrors, 'Your trial has expired')) {
      window.location.replace(ROUTES.LOCKED);
      return;
    }

    // If account was "churned" - logout
    if (errorsInclude(graphQLErrors, 'Your account is no longer available')) {
      Authentication.signOut();
      window.location.replace(ROUTES.SIGN_IN);
    }
  }
});

const defaultOptions = {
  watchQuery: {
    fetchPolicy: 'no-cache' as FetchPolicy,
    errorPolicy: 'ignore' as ErrorPolicy,
    addTypename: false,
  },
  query: {
    fetchPolicy: 'no-cache' as FetchPolicy,
    errorPolicy: 'all' as ErrorPolicy,
    addTypename: false,
  },
  mutate: {
    errorPolicy: 'none' as ErrorPolicy,
    addTypename: false,
  },
};

const stripTypenameFromQueryLink = new ApolloLink(
  (operation: Operation, forward: NextLink): Observable<FetchResult> => {
    operation.query = visit(operation.query, {
      Field(node: FieldNode): FieldNode | null | undefined {
        return node.name.value === '__typename' ? null : undefined;
      },
    });
    return forward(operation);
  }
);

export const gqlClient = new ApolloClient({
  link: ApolloLink.from([
    stripTypenameFromQueryLink,
    errorHandlerLink,
    traceAuthorCompleteLink,
    requestLink,
    retryLink,
    removeTypenameLink,
    httpLink,
  ]),
  cache: new InMemoryCache({
    addTypename: false,
  }),
  queryDeduplication: false,
  defaultOptions,
});

export type GqlCallVariables = Record<string, unknown>;

export interface GqlQueryOptions {
  abortSignal?: AbortSignal;
  authToken?: string;
}

interface GqlQueryParams {
  query: DocumentNode;
  variables?: GqlCallVariables;
  options?: GqlQueryOptions;
}

/**
 * Execute GQL query
 * @param query - GQL query
 * @param variables - query variables
 * @param options - additional options
 * @param options.abortSignal - AbortController signal
 * @param options.authToken - we can pass auth token here, if we want to use it instead of current global one
 */
export const executeGqlQuery = ({ query, variables, options }: GqlQueryParams) => {
  const params: QueryOptions = {
    query,
    variables,
    fetchPolicy: 'no-cache',
  };

  if (options?.abortSignal) {
    params.context = {
      fetchOptions: {
        signal: options.abortSignal,
      },
    };
  }

  if (options?.authToken) {
    params.context = {
      ...params.context,
      authToken: options.authToken,
    };
  }

  return gqlClient.query(params);
};

export interface GqlMutationOptions {
  abortSignal?: AbortSignal;
  traceAcRequests?: boolean;
  authToken?: string;
  /** Set to 'include' to send/store cookies (e.g. for EstablishSessionForOauth). */
  credentials?: 'include' | 'omit' | 'same-origin';
}

interface GqlMutationParams {
  mutation: DocumentNode;
  variables?: GqlCallVariables;
  options?: GqlMutationOptions;
}

/**
 * Execute GQL mutation
 * @param mutation - GQL mutation
 * @param variables - mutation variables
 * @param options - additional options
 * @param options.abortSignal - AbortController signal
 * @param options.traceAcRequests - should we trace AC requests
 * @param options.authToken - we can pass auth token here, if we want to use it instead of current global one
 */
export const executeGqlMutation = ({ mutation, variables, options }: GqlMutationParams) => {
  const params: MutationOptions = {
    mutation,
    variables,
  };

  if (options?.traceAcRequests) {
    const mutationName = getGqlOperationName(mutation);
    if (mutationName) {
      params.context = prepareACDebugContext(mutationName, variables || {});
    }
  }

  if (options?.abortSignal) {
    params.context = {
      ...params.context,
      fetchOptions: {
        signal: options.abortSignal,
      },
    };
  }

  if (options?.authToken) {
    params.context = {
      ...params.context,
      authToken: options.authToken,
    };
  }

  if (options?.credentials !== undefined) {
    params.context = {
      ...params.context,
      credentials: options.credentials,
    };
  }

  return gqlClient.mutate(params);
};
